import { Alert } from '@cmsgov/ds-medicare-gov';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import PasswordSection from '../../common/components/PasswordSection';
import UserNameSection from '../../common/components/UserNameSection';
import Div from '../../common/components/div';
import H1 from '../../common/components/h1';
import TranslatedSpan from '../../common/components/TranslatedSpan';
import getUrlParameters from '../../common/functions/getUrlParameters';
import translate from '../../common/functions/translate';
import { useConversion, useFormStart } from '../../common/hooks/use.analytics';
import useLoginApi, { LoginResponse, MfaInfo, MfaSendCodeResponse, SlsLoginErr, UpdateField } from '../../common/hooks/useLoginApi';
import { usePageTitle } from '../../common/hooks/usePageTitle';
import CreateAccountLinkSection from './CreateAccountLinkSection';
import LoginButtonSection from './LoginButtonSection';
import TroubleLoggingSection from './TroubleLoggingSection';
import UpdateLogin from './UpdateLogin';
import { useHistory, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { globalData } from '../../globalContext';
import { LoginFields, SmartErrorAlert } from '../../common/components/SmartErrorAlert';
import { InvalidField } from '../../common/functions/errorAlertContent/getAlertContent';
import { apiErrorContent } from '../../common/functions/errorAlertContent/loginErrors';
import { AxiosResponse } from 'axios';
import { pushWithParams } from '../../common/functions/pushWithParams';
import { hasErrorMessage } from '../../common/functions/getApiErrorMessage';
interface LoginErrorProps {
    showAlreadyUnlockedAlert?: boolean
}
export default function Form(props: LoginErrorProps) {
    const [showPassword, setShowPassword] = useState(false);
    const [showUpdateAccountPage, setShowUpdateAccountPage] = useState(false);
    const [showUsername, setShowUsername] = useState(false);
    const [showSecretQuestion, setShowSecretQuestion] = useState(false);
    const [showEmail, setShowEmail] = useState(false);
    const [usernameRef, setUsernameRef] = useState<HTMLElement | null>(null);
    const [passwordRef, setPasswordRef] = useState<HTMLElement | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const [password, setPassword] = useState('');
    const [username, setUsername] = useState('');
    const loginApi = useLoginApi();
    const showAlreadyUnlockedAlert = props.showAlreadyUnlockedAlert;
    const formStart = useFormStart('login');
    const conversion = useConversion('login', 'login');
    usePageTitle(translate('log_in_upper'));

    const [loginFailCount, setLoginFailCount] = useState(0);

    const [apiResponse, setApiResponse] = useState<AxiosResponse<LoginResponse> | AxiosResponse<MfaSendCodeResponse> | null>(null);
    const [invalidFields, setInvalidFields] = useState<InvalidField<LoginFields>[]>([]);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [usernameTouched, setUsernameTouched] = useState(false);

    type LoginLocationState = { logout: boolean, updateFields: UpdateField[] } | undefined;
    const location = useLocation<LoginLocationState>();
    const history = useHistory();

    const { showNewAccountLoginAlert, setShowNewAccountLoginAlert, setMfaLoginData, mfaLoginData } = useContext(globalData);

    // get SLS errors from query params
    const paramsChecked = useRef(false);

    // Show error message based on Error_code
    const handleErrorCode = useCallback((error_code: SlsLoginErr | undefined, fieldsToUpdate: UpdateField[] = []) => {
        switch (error_code) {
            case 'SSO-3':
                setLoginFailCount(prev => prev + 1);
                break;
            case 'SSO-4':
            case 'SSO-6':
                // needs to update password
                setShowPassword(true);
                setShowUpdateAccountPage(true);
                break;
            case 'SSO-7':
                history.push('/forgot-password', { adminPasswordReset: true, loginUsername: username });
                break;
            case 'SSO-13':
                //needs to update username, password, security question or any combinations of these
                if (fieldsToUpdate.includes('username'))
                    setShowUsername(true);
                if (fieldsToUpdate.includes('password'))
                    setShowPassword(true);
                if (fieldsToUpdate.includes('question'))
                    setShowSecretQuestion(true);
                if (fieldsToUpdate.includes('email'))
                    setShowEmail(true);

                setShowUpdateAccountPage(true);
                break;
            default:
                break;
        }

    }, [history, username]);

    useEffect(() => {
        if (paramsChecked.current) {
            return;
        }
        paramsChecked.current = true;

        const params = getUrlParameters();
        if (params.has('error')) {
            // Format the error as an API response so the <SmartErrorAlert /> can show the proper error here
            const errorCode = params.get('error')?.toUpperCase() as SlsLoginErr;
            setApiResponse({
                status: 400,
                statusText: '',
                headers: null,
                config: {},
                data: {
                    code: errorCode
                }
            });
            handleErrorCode(errorCode, location.state?.updateFields);
            // manually fire fail event because the conversion attempt (which allows this event
            // to go through) happened before we were redirected to this page
            conversion.failAPI(apiErrorContent.unknown.analyticsErr, true);

            // remove error param from URL and from saved query string
            params.delete('error');
            params.delete('error_code');
            history.replace(`/login?${params.toString()}`);
        }
    }, [location, conversion, history, handleErrorCode]);

    // for update account flow - load username from mfaLoginData
    const { username: savedUsername } = mfaLoginData;
    useEffect(() => {
        if (savedUsername) {
            setUsername(savedUsername);
        }
    }, [savedUsername]);

    const getNewAccountLoginAlert = () => {
        if (!showNewAccountLoginAlert)
            return null;

        return <>
            <Div isRow={true} className="ds-u-margin-top--3">
                <Div cols={12} colsMD={8}>
                    <Alert
                        hideIcon={true}
                        variation={'success'}
                    >
                        <TranslatedSpan
                            english={'you_all_set'}
                            className="ds-u-font-weight--semibold"
                        />
                        <p className="ds-u-margin--0">
                            <TranslatedSpan english={'you_can_login_and_get_confirmation_letter'}/>
                        </p>
                    </Alert>
                </Div>
            </Div>
        </>;
    };



    const handleMfaResponse = async (mfaInfo: MfaInfo) => {
        const numOptions = mfaInfo.configured_options.length;
        const hasOptedOut = mfaInfo.configured_options.some(x => x.opted_out);
        const hasDeactivated = mfaInfo.configured_options.some(x => x.deactivated);

        const redirectToLoginSendCode = () => {
            setMfaLoginData({
                username,
                password,
                options: mfaInfo.configured_options
            });
            pushWithParams(history, '/security-code/login-send-code');
        }
        if (numOptions === 1 && !(hasOptedOut || hasDeactivated)) {
            const [mfaOption] = mfaInfo.configured_options;
            const res = await loginApi.mfaSend(mfaOption.id);
            setApiResponse(res);

            const errors = res?.data.errors;
            if (errors && hasErrorMessage(errors, 'MFA_USER_OPTED_OUT')) {
                mfaOption.opted_out = true;
                redirectToLoginSendCode();
                return;
            }

            if (res?.status === 204) {
                setMfaLoginData({
                    username,
                    password,
                    type: mfaOption.type,
                    value: mfaOption.mfa_channel,
                    id: mfaOption.id
                });
                pushWithParams(history, '/security-code/login-enter-code');
            } else {
                setIsLoading(false);
            }
        } else {
            redirectToLoginSendCode();
            return;
        }

        return mfaInfo;
    }

    const onLogin = (event: React.FormEvent<HTMLFormElement>) => {
        if (isLoading) {
            return;
        }
        conversion.attempt();
        event.preventDefault();
        setApiResponse(null);
        setSubmitAttempted(true);
        const newInvalidFields: InvalidField<LoginFields>[] = [];
        if (!username) {
            newInvalidFields.push({ name: 'username', type: 'empty' });
        }
        if (!password) {
            newInvalidFields.push({ name: 'password', type: 'empty' });
        }
        setInvalidFields(newInvalidFields);
        if (newInvalidFields.length) {
            return;
        }

        setIsLoading(true);
        ssoLogin(username, password);
    };

    const ssoLogin = async (username: string, password: string) => {
        const res = await loginApi.login(username, password);
        if (!res) {
            return;
        }

        setApiResponse(res);
        setShowNewAccountLoginAlert(false);

        if (res.status === 200) {
            conversion.success();
            loginApi.loginSuccess(res, 'non-mfa');
        } else {
            const errorCode = res.data.code;
            if (errorCode === 'MFA_AUTH_REQUIRED' && res.data.mfa_info) {
                handleMfaResponse(res.data.mfa_info);
                return;
            }

            handleErrorCode(errorCode, res.data.mustUpdate); // mustUpdate will only be present for SSO-13
            setIsLoading(false); // leaving isLoading == true in the success case because MBP takes a long time to load
        }
    };

    const updatePassword = (value: string) => {
        formStart();
        const newValue = value.trimStart();
        setPassword(newValue);
    };

    const updateUsername = (value: string) => {
        formStart();
        const newValue = value.trimStart();
        setUsername(newValue);
    };

    const getPasswordError = () => {
        if (!passwordTouched && !submitAttempted) {
            return null;
        }
        return !password ? translate('enter_your_password') : null;
    };

    const getUsernameError = () => {
        if (!usernameTouched && !submitAttempted) {
            return null;
        }
        return !username ? translate('enter_your_usermame_with_period') : null;
    };

    return <>
        {!showUpdateAccountPage &&
        <Div
            isContainer={true}
            padding={3}
        >
            <form onSubmit={onLogin} method="POST">
                <fieldset style={{
                    border: 0,
                    margin: 0,
                    padding: 0,
                }}>
                    <legend>
                        <Div isRow>
                            <Div cols={12}>
                                <H1 className='ds-u-margin-bottom--3'>
                                    <TranslatedSpan
                                        english={'log_in_upper'}
                                        style={{whiteSpace: 'nowrap'}}
                                    />
                                </H1>
                            </Div>
                        </Div>
                    </legend>
                    <CreateAccountLinkSection/>
                    {showAlreadyUnlockedAlert &&
                        <Div isRow={true} marginTop={3}>
                            <Div cols={12} colsMD={8}>
                                <Alert
                                    heading={translate("your_account_already_unlocked")}
                                    hideIcon={true}
                                    variation={'success'}
                                >
                                    <p className="ds-u-margin--0">
                                        <Link to={'/login'}>{translate("log_in_upper")}</Link> {translate('now_start_using_account')} {' '}
                                        <Link to={'/forgot-username-and-password'}>{translate("get_help")}</Link> {translate('do_not_remember_your_username_and_password')}
                                    </p>
                                </Alert>
                            </Div>
                        </Div>
                    }
                    {getNewAccountLoginAlert()}
                    {location.state?.logout &&
                        <Div isRow className="ds-u-margin-top--3">
                            <Div cols={12} colsMD={8}>
                                <Alert
                                    hideIcon
                                    variation={'success'}
                                >
                                    <p className="ds-u-margin--0">
                                        {translate('you_successfully_logged_out')}
                                    </p>
                                </Alert>
                            </Div>
                        </Div>
                    }
                    <SmartErrorAlert
                        name="Login"
                        usernameRef={usernameRef}
                        passwordRef={passwordRef}
                        conversion={conversion}
                        apiResponse={apiResponse}
                        invalidFields={invalidFields}
                        onInvalidField={() => {}}
                        loginFailCount={loginFailCount}
                        parentHeading={translate('login')}
                    />
                    <UserNameSection
                        errorMessage={getUsernameError()}
                        inputRef={setUsernameRef}
                        label={translate('username')}
                        onBlur={() => setUsernameTouched(true)}
                        onChange={event => updateUsername(event.currentTarget.value)}
                        onFocus={formStart}
                        value={username}
                    />
                    <Div isRow>
                        <Div cols={10} colsMD={9} colsLG={6} colsXL={5}>
                            <PasswordSection
                                errorMessage={getPasswordError()}
                                id={'password-textbox'}
                                inputRef={setPasswordRef}
                                label={translate('password')}
                                onBlur={() => setPasswordTouched(true)}
                                onChange={event => updatePassword(event.currentTarget.value)}
                                onFocus={formStart}
                                showForgotPasswordLink={true}
                                value={password}
                            />
                        </Div>
                    </Div>
                </fieldset>
                <LoginButtonSection isLoading={isLoading}/>
                <TroubleLoggingSection/>
            </form>
        </Div>}
        {showUpdateAccountPage && <UpdateLogin
            showUsername={showUsername}
            showPassword={showPassword}
            showSecretQuestion={showSecretQuestion}
            showEmail={showEmail}
            username={username}
            backToLogin={() => {
                setShowUpdateAccountPage(false);
                setUsername('');
                setPassword('');
            }}
        />}
    </>;
}
